import { Action, MultiFactorStartSmsEnrollError, ThunkDispatch, ThunkAction } from '../types';
import queryString from 'query-string';
import { captureErrorMessage } from '../utilities/analytics/error-reporting';
import { UnsupportedCountry } from '../data/countries';

export const startEnrollResponse = (
  transactionToken: string,
  emailAddress: string,
  totpSecret: string,
  recoveryCode: string,
  totpUri: string
): Action => ({
  type: 'START_ENROLL_RESPONSE',
  transactionToken,
  emailAddress,
  totpSecret,
  recoveryCode,
  totpUri,
});

export const requestStartEnroll = (token: string): Action => ({
  type: 'START_ENROLL',
  token,
});

export const startEnroll =
  (token: string): ThunkAction =>
  (dispatch: ThunkDispatch, getState) => {
    dispatch(requestStartEnroll(token));

    const { csrfToken } = getState();

    return fetch(
      `/manage/rest/two-step-verification/start-enroll?${queryString.stringify({
        token,
      })}`,
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'X-CSRF-TOKEN': csrfToken },
      }
    )
      .then(response =>
        response.json().then(data => {
          if (response.ok) {
            return data;
          } else {
            throw new Error(
              `${response.status} ${response.statusText}: /manage/rest/two-step-verification/start-enroll`
            );
          }
        })
      )
      .then(({ transactionToken, emailAddress, totpSecret, recoveryCode, totpUri }) => {
        dispatch(
          startEnrollResponse(transactionToken, emailAddress, totpSecret, recoveryCode, totpUri)
        );
      });
  };

export const confirmEnrollResponse = (redirectUrl: string | null | undefined): Action => ({
  type: 'CONFIRM_ENROLL_RESPONSE',
  redirectUrl,
});

export const confirmEnrollError = (): Action => ({
  type: 'CONFIRM_ENROLL_ERROR',
});

export const requestConfirmEnroll = (
  otpCode: string,
  continueUrl: string | null | undefined
): Action => ({
  type: 'CONFIRM_ENROLL',
  otpCode,
  continueUrl,
});

export const confirmEnroll =
  (otpCode: string, continueUrl: string | null | undefined): ThunkAction =>
  (dispatch, getState) => {
    dispatch(requestConfirmEnroll(otpCode, continueUrl));

    const { csrfToken, mfaEnrollment } = getState();

    return fetch(
      `/manage/rest/two-step-verification/confirm-enroll?${queryString.stringify({
        token: mfaEnrollment.token,
        continue: continueUrl,
      })}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken,
        },
        body: JSON.stringify({
          transactionToken: mfaEnrollment.transactionToken,
          otpCode,
        }),
        credentials: 'same-origin',
      }
    )
      .then(response => {
        switch (response.status) {
          case 200:
            return response.json();
          case 204:
            return Promise.resolve({ redirectUrl: null });
          default:
            throw response;
        }
      })
      .then(
        ({ redirectUrl }) => {
          dispatch(confirmEnrollResponse(redirectUrl));
        },
        response => {
          dispatch(confirmEnrollError());
          throw new Error(
            `${response.status} ${response.statusText}: /manage/rest/two-step-verification/confirm-enroll`
          );
        }
      );
  };

export const startSmsEnrollResponse = (
  transactionToken: string,
  recoveryCode: string,
  phoneNumber: string
): Action => ({
  type: 'START_SMS_ENROLL_RESPONSE',
  transactionToken,
  recoveryCode,
  phoneNumber,
});

export const startSmsEnrollError = (smsEnrollError: MultiFactorStartSmsEnrollError): Action => ({
  type: 'START_SMS_ENROLL_ERROR',
  smsEnrollError,
});

export const resetSmsEnrollError = (): Action => ({
  type: 'RESET_SMS_ENROLL_ERROR',
});

export const requestStartSmsEnroll = (phoneNumber: string, token: string): Action => ({
  type: 'START_SMS_ENROLL',
  token,
  phoneNumber,
});

export const startSmsEnroll =
  (phoneNumber: string, token: string, captchaToken?: string | undefined): ThunkAction =>
  (dispatch, getState) => {
    dispatch(requestStartSmsEnroll(phoneNumber, token));

    const { csrfToken } = getState();
    return fetch(
      `/manage/rest/two-step-verification/start-enroll-sms?${queryString.stringify({
        token,
      })}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken,
        },
        body: JSON.stringify({
          phoneNumber,
          captchaToken,
        }),
        credentials: 'same-origin',
      }
    )
      .then(response => {
        switch (response.status) {
          case 200:
            return response.json();
          default:
            throw response;
        }
      })
      .then(
        ({ transactionToken, recoveryCode }) => {
          dispatch(startSmsEnrollResponse(transactionToken, recoveryCode, phoneNumber));
        },
        async response => {
          const errorFromBadRequest: (
            response: Response
          ) => Promise<MultiFactorStartSmsEnrollError> = response =>
            response
              .json()
              .then(body => {
                switch (body.type) {
                  case 'invalidPhoneNumber':
                    return 'invalid_phone_number';
                  case 'phoneNumberNotEligible':
                    return 'phone_number_not_eligible';
                  default:
                    return 'generic_error';
                }
              })
              .catch(() => 'generic_error');
          const error = response => {
            switch (response.status) {
              case 400:
                return errorFromBadRequest(response);
              case 429:
                return 'too_many_sms';
              default:
                return 'generic_error';
            }
          };
          const errorLabel = await error(response);
          dispatch(startSmsEnrollError(errorLabel));
          if (errorLabel === 'generic_error') {
            captureErrorMessage(
              `${response.status} ${response.statusText}: /manage/rest/two-step-verification/start-enroll-sms`
            );
          }
        }
      );
  };

export const updateSmsEnrollmentUnsupportedCountries = (
  unsupportedCountries: UnsupportedCountry[] = []
): Action => ({
  type: 'UPDATE_SMS_ENROLLMENT_UNSUPPORTED_COUNTRIES',
  unsupportedCountries,
});

export const fetchUnsupportedCountries = (): ThunkAction => dispatch => {
  return fetch(`/v2/mfa/enroll-sms/countries`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'same-origin',
  })
    .then(response => {
      switch (response.status) {
        case 200:
          return response.json();
        default:
          return [];
      }
    })
    .then(
      body => {
        dispatch(updateSmsEnrollmentUnsupportedCountries(body.unsupportedCountries || []));
      },
      () => {
        dispatch(updateSmsEnrollmentUnsupportedCountries([]));
      }
    );
};

export const smsEnrollmentCaptchaFailure = (isOnline: boolean): Action =>
  startSmsEnrollError(isOnline ? 'captcha' : 'generic_error');

export const startSecurityKeyEnroll = (token: string): Action => ({
  type: 'START_SECURITY_KEY_ENROLL',
  token,
});

export const confirmSecurityKeyEnrollment = (recoveryCode: string): Action => ({
  type: 'CONFIRM_SECURITY_KEY_ENROLLMENT',
  recoveryCode,
});
